import { Cost, CostCalculation, CostCalculationEntry, ProjectService } from 'entities';

export const operations = {
  SUM: {
    resolve: (valueA: string | number, valueB: string | number): number =>
      Number(valueA) + Number(valueB),
  },

  SUBTRACT: {
    resolve: (valueA: string | number, valueB: string | number): number =>
      Number(valueA) - Number(valueB),
  },

  MULTIPLY: {
    resolve: (valueA: string | number, valueB: string | number): number =>
      Number(valueA) * Number(valueB),
  },

  DIVIDE: {
    resolve: (valueA: string | number, valueB: string | number): number =>
      Number(valueA) / Number(valueB),
  },

  AS_TOTAL: {
    resolve: (valueA: string | number): number => Number(valueA),
  },
};

type ServiceConfig = {
  costCalculation: CostCalculation[];
  costs: Cost[];
};

type ResolvedValue = { currency: string; result: number };

function resolveSingleCalculation(
  calculation: CostCalculation,
  service: ServiceConfig,
): ResolvedValue {
  function resolveValue(entry: CostCalculationEntry): ResolvedValue {
    const { costs, costCalculation } = service;

    if (entry.type === 'VARIABLE') {
      const variable = costs.find(({ id }) => id === entry.ref);

      return { currency: variable?.currency || '', result: Number(variable?.value ?? 0) };
    }

    const calculationReferenced = costCalculation.find(({ id }) => id === entry.ref);

    if (!calculationReferenced) return { currency: '', result: 0 };

    return resolveSingleCalculation(calculationReferenced, service);
  }

  const { base, operation, value } = calculation;

  const { resolve } = operations[operation];

  const { currency, result: baseResult } = resolveValue(base);
  const secondValue = value ? resolveValue(value) : { currency: '', result: 0 };

  const numberValue = resolve(baseResult, secondValue.result);

  return {
    currency: currency || secondValue.currency,

    result: numberValue,
  };
}

function resolveReferences(
  reference: CostCalculationEntry,
  service: ServiceConfig,
): ServiceConfig['costs'] {
  const { type, ref } = reference;

  if (type === 'VARIABLE') return service.costs.filter(({ id }) => id === ref);

  const calcRef = service.costCalculation.find(({ id }) => id === ref);

  if (!calcRef) return [];

  const { base, value } = calcRef;

  const toResolve = [base, value].filter(Boolean) as CostCalculationEntry[];

  return toResolve.map((entry) => resolveReferences(entry, service).flat()).flat();
}

function validadeCurrencyRef({ base, value }: CostCalculation, service: ServiceConfig): boolean {
  const refs = [base, value].filter(Boolean) as CostCalculationEntry[];

  const resolved = refs.map((ref) => resolveReferences(ref, service)).flat();

  return !!resolved.find(({ type }) => type === 'CURRENCY');
}

function getLastCurrencyReference(service: ServiceConfig): CostCalculation | undefined {
  const lastCurrencyRef = service.costCalculation
    .slice()
    .reverse()
    .find((calculation) => validadeCurrencyRef(calculation, service));

  return lastCurrencyRef;
}

export function getCalculationReference(service: ServiceConfig): CostCalculation | undefined {
  const asTotalCalc = service.costCalculation.find(({ operation }) => operation === 'AS_TOTAL');

  const calculationRef = asTotalCalc || getLastCurrencyReference(service);

  return calculationRef;
}

export function resolveExpression(service: ServiceConfig): ResolvedValue {
  const calculationRef = getCalculationReference(service);

  if (!calculationRef) return { currency: 'USD', result: 0 };

  return resolveSingleCalculation(calculationRef, service);
}

export function calculateCountryServiceCost(
  variables: Cost[],
  costCalculation: CostCalculation[],
): number {
  const { result } = resolveExpression({
    costs: variables,
    costCalculation: costCalculation || [],
  });

  return result;
}

export function calculateServiceCost(service: ProjectService): string {
  const { costCalculation, costs } = service;

  const total = Object.values(costs ?? {}).reduce((singleCostAcc, variables) => {
    const result = calculateCountryServiceCost(variables, costCalculation);

    return singleCostAcc + result;
  }, 0);

  return total ? total.toFixed(2) : '0.00';
}

export function calculateServiceCountryCost(service: ProjectService, country: string): string {
  return calculateServiceCost({ ...service, costs: { [country]: service.costs[country] } });
}

export function calculateProjectTotal(services: ProjectService[]): string {
  const totals = services.reduce((acc, projectService) => {
    return acc + Number(calculateServiceCost(projectService));
  }, 0);

  return totals ? totals.toFixed(2) : '0.00';
}

export function calculateProjectCountryTotal(services: ProjectService[], country: string): string {
  return calculateProjectTotal(
    services.map((service) => ({
      ...service,
      costs: { [country]: service.costs[country] },
    })),
  );
}
